
import React from "react";
import { Capability, Schemes, Utils } from "../../utils";
import { i18nManager } from "../../i18n";
import { PreviewLink } from "./PreviewLink";
import { Button, Classname, ColorHelper, ColorPicker, ColorPickerManageRef, ComponentRenderType, NoseurObject, PopoverManageRef, StringHelper, useScreenDimension } from "@rivtn/noseur";
import { Elements } from "../shared/Elements";
import { ActivePageDesign, BaseService, IPageDesign, SanitizedResponse, UserLandService } from "../../services";
import { Response } from "kyofuuc";
import UpSellPlan from "../shared/UpSellPlan";

export interface PageDesignProps {
    publicId: string;
    designType: string;
    capability: string;
    planLevel?: number;
    noControl?: boolean;
    activeDesign?: ActivePageDesign;
    searchParams?: NoseurObject<any>;
    secondaryView?: React.ReactElement;
    leftPanelStyle?: React.CSSProperties;
    secondaryControl?: React.ReactElement;
    onUpdate: (design: ActivePageDesign, done: () => void) => void;
    header?: {
        desc?: string;
        title?: string;
    };
}

function PageDesign(props: PageDesignProps) {

    const planLevel = props.planLevel ?? 1;
    const labels = i18nManager.Labels.page_design;
    const { height, width } = useScreenDimension();
    const designType = props.designType ?? "unknown";
    const userLandService = UserLandService.getInstance();
    const [autoReset, setAutoReset] = React.useState(true);
    const [activeColor, setActiveColor] = React.useState<string>();
    const [pageDesigns, setPageDesigns] = React.useState<IPageDesign[]>();
    const colorPickerManageRef = React.useRef<ColorPickerManageRef>(null);
    const [selectedPageDesign, setSelectedPageDesign] = React.useState<IPageDesign | undefined>();
    const [activePageDesign, setActivePageDesign] = React.useState<ActivePageDesign | undefined>(props.activeDesign);

    React.useEffect(() => {
        userLandService.getUserLandDesigns(designType).then(({ data }: Response) => {
            const designs: IPageDesign[] = (typeof data === "string" ? [] : data?.sort((a: any, b: any) => a.plan_level - b.plan_level));
            setSelectedPageDesign(designs?.find((pd) => pd.id === props.activeDesign?.design_id) ?? designs?.[0]);
            setPageDesigns(designs);
        }).catch(BaseService.reportError);
    }, []);

    if (!pageDesigns) return (Elements.LOADING_STATE2);

    return (<div className="page-design">
        {(props.header?.title || props.header?.desc) && (<div className="header">
            <span className="t" dangerouslySetInnerHTML={{ __html: props.header?.title ?? "" }} />
            <span className="d" dangerouslySetInnerHTML={{ __html: props.header?.desc ?? "" }} />
        </div>)}
        <div className="container">
            <div className="left-panel">
                <span className="t">{labels.select_design}</span>
                <div className="designs" style={props.leftPanelStyle}>
                    {pageDesigns?.map((pageDesign) => {
                        return (<div key={pageDesign.name} className={Classname.build("design", { "active": pageDesign.name === selectedPageDesign?.name })} onClick={() => {
                            setSelectedPageDesign(pageDesign);
                            if (autoReset) {
                                setActivePageDesign({
                                    ...activePageDesign as any, scheme_settings: {
                                        ...activePageDesign?.scheme_settings,
                                        colors: (props.activeDesign?.design_id === pageDesign?.id ? props.activeDesign.scheme_settings?.colors : {})
                                    }
                                });
                            }
                        }}>
                            <img src={pageDesign.preview_image} alt={pageDesign.name} />
                            <span>{pageDesign.name}</span>
                            {(pageDesign.plan_level > planLevel) && (<i className="fa fa-star" />)}
                        </div>);
                    })}
                </div>
                <span className="hint">{labels.scroll_down_for_more}</span>
                {(!props.noControl && selectedPageDesign) && (<div className="actions">
                    <span className="link" onClick={() => PreviewLink.mobile(userLandService.getPreviewUrl(designType, props.publicId!, selectedPageDesign, activePageDesign, props.searchParams), width < 800)}>{labels.mobile_preview}</span>
                    <span className="link" style={{ marginBottom: 20 }} onClick={() => PreviewLink.desktop(userLandService.getPreviewUrl(designType, props.publicId!, selectedPageDesign, activePageDesign, props.searchParams), width < 800)}>{labels.desktop_preview}</span>
                    <UpSellPlan planLevel={selectedPageDesign.plan_level} actionCapability={props.capability}
                        childrenRelayProps={{ readOnly: true, leftIcon: "fa fa-arrow-circle-up", scheme: Schemes.GOLDEN, style: { padding: "10px 35px" } }} >
                        <Button style={{ width: "100%" }} scheme={Schemes.OMNEHOST} text={labels.update_design} /*disabled={!!(selectedPageDesign?.plan_level && selectedPageDesign?.plan_level > planLevel)}**/ loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={(e) => {
                            e.manageRef?.setLoadingState(true);
                            if (activePageDesign) activePageDesign.design_id = selectedPageDesign?.id;
                            props.onUpdate?.(activePageDesign ?? {} as any, () => {
                                e.manageRef?.setLoadingState(false);
                            });
                        }} />
                    </UpSellPlan>
                </div>)}
                {props.secondaryControl}
            </div>
            <div className="main-panel">
                {!!(Object.keys(selectedPageDesign?.colors ?? {})?.length) && (<>
                    <div className="headr">
                        <span className="t">{labels.customize}</span>
                        <span className="link" style={{ marginLeft: 10 }} onClick={() => randomizeOrResetDesignScheme(true)}>{labels.reset}</span>
                        <span className="link" style={{ marginLeft: 15 }} onClick={() => randomizeOrResetDesignScheme()}>{labels.randomize}</span>
                    </div>
                    <div className="customizables">
                        {Object.entries(selectedPageDesign?.colors!)?.map(([name, value]) => {
                            const backgroundColor = activePageDesign?.scheme_settings?.colors?.[name] ?? value;
                            const label = StringHelper.toSaneSentenceFormat(name);
                            /*const label = typeof customizable.label === "string"
                                ? customizable.label
                                : customizable.label[i18nManager.ActiveLanguage] ?? Object.values(customizable.label)[0];*/

                            return (<div key={label} className="customizable">
                                <span>{label}</span>
                                <div style={{ backgroundColor }} onClick={(e: any) => {
                                    setActiveColor(name);
                                    colorPickerManageRef.current?.toggle(e, undefined, [ColorHelper.hexToColor(backgroundColor!)!]);
                                }} />
                            </div>);
                        })}
                    </div>
                </>)}
                {(selectedPageDesign && (height > 900 && width > 800)) && (<iframe src={userLandService.getPreviewUrl(designType, props.publicId!, selectedPageDesign, activePageDesign, props.searchParams)} />)}
                {props.secondaryView}
            </div>
        </div>

        <ColorPicker type={ComponentRenderType.POPOVER} manageRef={colorPickerManageRef} onSelectColor={(e) => {
            if (!activeColor) return;
            const newActivePageDesign = { ...(activePageDesign ?? {}) } as ActivePageDesign;
            if (!newActivePageDesign.scheme_settings) newActivePageDesign.scheme_settings = {};
            if (!newActivePageDesign.scheme_settings.colors) newActivePageDesign.scheme_settings.colors = {};
            newActivePageDesign.scheme_settings!.colors![activeColor!] = ColorHelper.hexWithAlpha(e.color.hex, e.color.alpha);
            setActivePageDesign(newActivePageDesign);
        }} />
    </div>);

    function randomizeOrResetDesignScheme(reset?: boolean) {
        const newActivePageDesign = { ...(activePageDesign ?? {}) } as ActivePageDesign;
        if (!newActivePageDesign.scheme_settings) newActivePageDesign.scheme_settings = {};
        if (!newActivePageDesign.scheme_settings.colors) newActivePageDesign.scheme_settings.colors = {};
        Object.entries(selectedPageDesign?.colors ?? {}).forEach(([name, value]) => {
            newActivePageDesign.scheme_settings!.colors![name] = (reset ? value : ColorHelper.rgbToHex(ColorHelper.similarColor(ColorHelper.hexToRgb(value), 0.5)));
        });
        setActivePageDesign(newActivePageDesign);
    }

}

export default PageDesign;
